import { BIZ_APPLICATION } from '../../types'
import moment, { Moment } from 'moment'
import _ from 'lodash';
import * as constants from './constants';
import { Entity } from '../../explorer/types';

// Server Side oriented Milestones
export enum Milestone {
    Src = 'src',
    Bin = 'bin',
    Db = 'db',
    Backend = 'backend',
    Running = 'start',
    Ping = 'ping',
    Stopped = 'stop',
    Erased = 'erase'
}

export const ALL_MILESTONES_ORDERED: Milestone[] = [
    Milestone.Src,
    Milestone.Bin,
    Milestone.Db,
    Milestone.Backend,
    Milestone.Running,
    Milestone.Stopped,
    Milestone.Erased
];

// Cient Side oriented Phases (some of them but not all correspond to Milestones)
export enum AppPhase {
    Fetch = 'Fetch',
    Create = 'Create',
    Compile = 'Compile',
    Init = 'Init',
    Backend = 'Backend',
    Start = 'Start',
    Ping = 'Ping', // clent side only
    Stop = 'Stop',
    Destroy = 'Destroy'
}

export interface Application {
    name: string;
    authorName: string;
    appPath: string;
    milestones?: { [key in Milestone]: MilestoneState };
    getSuccessMilestones: () => Milestone[]; // takes into account all three 'state' fields: started, success and error;
    getAvailMilestones: () => Milestone[]; // takes into account all three 'state' fields: started, success and error;
    getErrorMilestones: () => Milestone[]; // takes into account all three 'state' fields: started, success and error;
    getPendingMilestones: () => Milestone[]; // takes into account all three 'state' fields: started, success and error;
    isSuccess: (milestones: Milestone[]) => boolean;
    isAvail: (milestones: Milestone[]) => boolean;
    isError: (milestones: Milestone[]) => boolean;
    isPending: (milestones: Milestone[]) => boolean;
    getMoment: (milestone: Milestone, status: MilestoneDetail) => Moment | null;
    getEntity: () => Entity;
    port?: number;
    id?: number;
    migration?: Milestone; // Although defined as Milestone, can only be one of: (Milestone.Bin, Milestone.Db, Milestone.Backend, Milestone.Running) - see corresponding server side enum.
}

export class ApplicationImpl implements Application {
    private entity: any
    private _milestones: { [key in Milestone]: Map<MilestoneDetail, Moment | null> } = {
        [Milestone.Src]: new Map<MilestoneDetail, Moment | null>(),
        [Milestone.Bin]: new Map<MilestoneDetail, Moment | null>(),
        [Milestone.Db]: new Map<MilestoneDetail, Moment | null>(),
        [Milestone.Backend]: new Map<MilestoneDetail, Moment | null>(),
        [Milestone.Running]: new Map<MilestoneDetail, Moment | null>(),
        [Milestone.Ping]: new Map<MilestoneDetail, Moment | null>(),
        [Milestone.Stopped]: new Map<MilestoneDetail, Moment | null>(),
        [Milestone.Erased]: new Map<MilestoneDetail, Moment | null>(),
    };
    // 

    get name() { return this.entity.name }
    get authorName() { return this.entity.authorName }

    get appPath() {
        return this.entity.appPath;
    }

    get port() { return this.entity.port != null ? parseInt(this.entity.port) : undefined }
    get id() { return this.entity['_id'] != null ? parseInt(this.entity['_id']) : undefined }

    get milestones() { return this._milestones }
    set milestones(value: { [key in Milestone]: MilestoneState }) { this._milestones = value }

    get migration() {
        switch (this.entity.migration) {
            case 'BIN':
                return Milestone.Bin;
            case 'DB':
                return Milestone.Db;
            case 'BACKEND':
                return Milestone.Backend;
            case 'RUNNING':
                return Milestone.Running;
            default:
                return undefined;
        }
    }

    constructor(entity: any) {
        this.entity = entity;

        [
            Milestone.Src,
            Milestone.Bin,
            Milestone.Db,
            Milestone.Backend,
            Milestone.Running,
            Milestone.Stopped,
            Milestone.Erased
        ].forEach(milestone => {
            const state = new Map();
            this._milestones[milestone] = state;
            [
                MilestoneDetail.Error,
                MilestoneDetail.Success,
                MilestoneDetail.Started
            ].forEach(status => {
                const value: Moment = moment(entity[`${milestone}${status}`]);
                state.set(status, value.isValid() ? value : null);
            })
        })
    }

    getPendingMilestones: () => Milestone[] = () => {
        const keys: Milestone[] = (Object.keys(this._milestones) as Milestone[])
            .filter(m => m !== Milestone.Ping);
        const pending = keys.filter(m => {
            return (
                this._milestones[m]!.get(MilestoneDetail.Started) !== null
                && this._milestones[m]!.get(MilestoneDetail.Success) == null
                && this._milestones[m]!.get(MilestoneDetail.Error) == null
            )
        })
        return pending;
    }

    getSuccessMilestones: () => Milestone[] = () => {
        const keys: Milestone[] = (Object.keys(this._milestones) as Milestone[])
            .filter(m => m !== Milestone.Ping);;
        const success = keys.filter(m => {
            return (
                this._milestones[m]!.get(MilestoneDetail.Started) !== null
                && this._milestones[m]!.get(MilestoneDetail.Success) !== null
                && this._milestones[m]!.get(MilestoneDetail.Error) === null
            )
        })
        return success;
    }

    getAvailMilestones: () => Milestone[] = () => {
        const keys: Milestone[] = (Object.keys(this._milestones) as Milestone[])
            .filter(m => m !== Milestone.Ping);
        const avail = keys.filter(m => {
            return (
                this._milestones[m]!.get(MilestoneDetail.Success) === null
                && this._milestones[m]!.get(MilestoneDetail.Error) === null
            )
        })
        return avail;
    }

    getErrorMilestones: () => Milestone[] = () => {
        const keys: Milestone[] = (Object.keys(this._milestones) as Milestone[])
            .filter(m => m !== Milestone.Ping);
        const err = keys.filter(m => {
            return (
                this._milestones[m]!.get(MilestoneDetail.Error) !== null
            )
        })
        return err;
    }

    isSuccess: (milestones: Milestone[]) => boolean = (milestones) => {
        const successful: Milestone[] = this.getSuccessMilestones();
        return _.difference(milestones, successful).length === 0;

    }

    isAvail: (milestones: Milestone[]) => boolean = (milestones) => {
        const ready: Milestone[] = this.getAvailMilestones();
        return _.difference(milestones, ready).length === 0;
    }

    isError: (milestones: Milestone[]) => boolean = (milestones) => {
        const error: Milestone[] = this.getErrorMilestones();
        return _.difference(milestones, error).length === 0;
    }

    isPending: (milestones: Milestone[]) => boolean = (milestones) => {
        const pending: Milestone[] = this.getPendingMilestones();
        return _.difference(milestones, pending).length === 0;
    }

    getMoment: (milestone: Milestone, status: MilestoneDetail) => Moment | null = (milestone, status) => {
        let successMoment: Moment | null = null;
        const mState: MilestoneState | undefined = this._milestones[milestone];
        if (mState != undefined) {
            const sMoment: Moment | null | undefined = mState.get(status);
            if (sMoment !== undefined) {
                successMoment = sMoment;
            }
        }
        return successMoment;
    }

    getEntity: () => Entity = () => {
        return this.entity;
    };
}

export interface ApplicationState {
    application: Application | null
    optimisticStart: {
        milestone: Milestone;
        timestamp: Moment;
    } | null;
    phases: { [key in AppPhase]: PhaseStatus };
    isFetching: boolean
    isPinging: boolean
    lastPinged: Moment | null
    pingTimedOut: boolean
    heartbeatFail: boolean
    // isBuilding: boolean // global build ('arcade' Launch button)
    interrupt: boolean
}



export interface PhaseStatus {
    timedout: boolean
}

export enum MilestoneDetail {
    Started = 'Start',
    Success = 'Success',
    Error = 'Error'
}

export type MilestoneState = Map<MilestoneDetail, Moment | null>;

export interface SetPhaseStatusAction {
    type: typeof constants.SET_PHASE_STATUS;
    phase: AppPhase;
    status: PhaseStatus;
}

export interface CheckPendingAction {
    type: typeof constants.CHECK_PENDING;
    application: Application;
}

// Fetch
export interface StartFetchAction {
    type: typeof constants.START_FETCH_APP;
    id: number;
}

export interface SuccessFetchAction {
    type: typeof constants.SUCCESS_FETCH_APP;
    application: Application;
}

// Create
export interface StartCreateAction {
    type: typeof constants.START_CREATE;
    id: number;
}

export interface SuccessCreateAction {
    type: typeof constants.SUCCESS_CREATE;
}

// Destroy
export interface StartDestroyAction {
    type: typeof constants.START_DESTROY;
    id: number;
}

export interface SuccessDestroyAction {
    type: typeof constants.SUCCESS_DESTROY;
}

// Compile
export interface StartCompileAction {
    type: typeof constants.START_COMPILE;
    id: number;
}

export interface SuccessCompileAction {
    type: typeof constants.SUCCESS_COMPILE
}

// Init
export interface StartInitAction {
    type: typeof constants.START_INIT;
    id: number;
}

export interface SuccessInitAction {
    type: typeof constants.SUCCESS_INIT;
}

// Build backend
export interface StartBuildBackendAction {
    type: typeof constants.START_BUILD_BACKEND;
    id: number;
}

export interface SuccessBuildBackendAction {
    type: typeof constants.SUCCESS_BUILD_BACKEND;
}

// Start
export interface StartStartAction {
    type: typeof constants.START_START;
    id: number;
}

export interface SuccessStartAction {
    type: typeof constants.SUCCESS_START;
}

// Launch
export interface StartLaunchAction {
    type: typeof constants.LAUNCH_START;
    id: number;
    name: string;
}

export interface SuccessLaunchAction {
    type: typeof constants.LAUNCH_SUCCESS;
}

// Stop
export interface StartStopAction {
    type: typeof constants.START_STOP;
    id: number;
}

export interface OptimisticStartAction {
    type: typeof constants.OPTIMISTIC_START;
    milestone: Milestone;
    timestamp: Moment;
}

// Heartbeat Ping
export interface HeartbeatStartAction {
    type: typeof constants.HEARTBEAT_START;
    name: string;
}

export interface HeartbeatSuccessAction {
    type: typeof constants.HEARTBEAT_SUCCESS;
}

export interface HeartbeatFailureAction {
    type: typeof constants.HEARTBEAT_FAILURE;
}

export interface HeartbeatStopAction {
    type: typeof constants.HEARTBEAT_STOP;
}

export type HearbeatAction = HeartbeatStartAction | HeartbeatSuccessAction | HeartbeatFailureAction | HeartbeatStopAction;

// Launch Ping
export interface StartPingingAction {
    type: typeof constants.START_PINGING;
    timeout: number;
    name: string;
}

export interface DoStartPingingAction {
    type: typeof constants.DO_START_PINGING
}

export interface SuccessPingAction {
    type: typeof constants.SUCCESS_PING;
}

export interface FailPingAction {
    type: typeof constants.FAIL_PING;
}

export interface PingingTimeoutAction {
    type: typeof constants.PINGING_TIMEOUT;
}

export interface SuccessStopAction {
    type: typeof constants.SUCCESS_STOP
}

export interface StartBuildAction {
    type: typeof constants.START_BUILD;
    id: number;
}

// export interface EndBuildAction {
//     type: typeof constants.END_BUILD;
// }

export interface ResetAction {
    type: typeof constants.RESET;
}

export type ApplicationActions =
    StartFetchAction |
    SuccessFetchAction |

    CheckPendingAction |

    StartCreateAction |
    SuccessCreateAction |

    StartDestroyAction |
    SuccessDestroyAction |

    StartCompileAction |
    SuccessCompileAction |

    StartInitAction |
    SuccessInitAction |

    StartBuildBackendAction |
    SuccessBuildBackendAction |

    StartStartAction |
    SuccessStartAction |

    StartPingingAction |
    DoStartPingingAction |
    SuccessPingAction |
    FailPingAction |
    PingingTimeoutAction |

    StartStopAction |
    SuccessStopAction |

    StartBuildAction |
    // EndBuildAction |

    StartLaunchAction |
    SuccessLaunchAction |

    HearbeatAction |

    SetPhaseStatusAction |
    OptimisticStartAction |
    ResetAction;

// Manage Application Specific
export interface ApplicationOptions {
    admin?: boolean;
}

export interface ApplicationConf {
    type: typeof BIZ_APPLICATION;
    options?: ApplicationOptions;
}